import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowLeft, X, Check, CreditCard, Receipt } from "lucide-react";
import NavbarLog from "../NavbarLog/NavbarLog";
import "./news.css";

/* ── Subscription plans ── */
const SUBSCRIPTION_PLANS = [
  { id: "4months", label: "4 months", price: 120, perMonth: 30 },
  { id: "6months", label: "6 months", price: 162, perMonth: 27, badge: "Save 10%" },
  { id: "1year", label: "1 year", price: 240, perMonth: 20, badge: "Save 33%" },
];

const PAYMENT_METHODS = [
  { label: "Tunisian Post", value: "poste" },
  { label: "D17", value: "d17" },
  { label: "IZI", value: "izi" },
  { label: "FLOUCI", value: "flouci" },
  { label: "Western Union", value: "western Union" },
  { label: "Binance", value: "binance" },
  { label: "Bitget", value: "bitget" },
  { label: "BingX", value: "bingx" },
];

const NEWS_ITEMS = [
  {
    id: "annual-membership-2026",
    date: "2026-01-01",
    title:
      "Important Announcement: Launching the New Annual Membership System for an Integrated Digital Experience",
    paragraphs: [
      "As part of our continuous efforts to enhance service quality and deliver advanced digital solutions that meet your expectations, we are pleased to announce a new update regarding the access and utilization of our online platform.",
      "Starting from 2026, an annual subscription system will be implemented as a prerequisite for website access. This system will grant our valued clients exclusive and full access to all available features and services, with the subscription renewing automatically on a yearly basis.",
    ],
    bulletsTitle: "⚡ What does the new system guarantee?",
    bullets: [
      { label: "Advanced Security", text: "Superior data protection ensuring a 100% secure and reliable user environment." },
      { label: "Exclusive Features", text: "Priority access to the latest tools, services, and updates as soon as they are launched." },
      { label: "Continuity & Development", text: "Dedicated technical support and regular updates ensuring optimal digital performance throughout the year." },
    ],
    hasSubscriptionCTA: true,
  },
];

const formatDate = (isoDate) =>
  new Date(isoDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

/* ══════════════════════════════════════════════
   Step indicator
   ══════════════════════════════════════════════ */
const RENEW_STEPS = [
  { id: "plan", label: "Plan" },
  { id: "payment", label: "Payment" },
];

const RenewStepIndicator = ({ step }) => {
  const currentIndex = RENEW_STEPS.findIndex((s) => s.id === step);
  return (
    <div className="sub-steps">
      {RENEW_STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div
            className={`sub-step${
              i === currentIndex ? " sub-step--active" : i < currentIndex ? " sub-step--done" : ""
            }`}
          >
            <span className="sub-step__circle">
              {i < currentIndex ? <Check size={12} /> : i + 1}
            </span>
            <span className="sub-step__label">{s.label}</span>
          </div>
          {i < RENEW_STEPS.length - 1 && (
            <div className={`sub-step__line${i < currentIndex ? " sub-step__line--done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════════════
   Subscription block — displayed IN PLACE inside the popup
   ══════════════════════════════════════════════ */
const SubscriptionRenewalInline = ({ onBack, onDone }) => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [step, setStep] = useState("plan");
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [payment, setPayment] = useState("");
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [receiptName, setReceiptName] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      setErrors((er) => ({ ...er, paymentReceipt: "Please upload an image or PDF file." }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors((er) => ({ ...er, paymentReceipt: "File size must be less than 10MB." }));
      return;
    }
    setPaymentReceipt(file);
    setReceiptName(file.name);
    setErrors((er) => ({ ...er, paymentReceipt: "" }));
  };

  const goToPayment = () => {
    if (!subscriptionPlan) {
      setErrors((er) => ({ ...er, subscriptionPlan: "Please choose a subscription." }));
      return;
    }
    setStep("payment");
  };

  const handleBack = () => {
    if (step === "payment") {
      setStep("plan");
    } else {
      onBack();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = {};
    if (!payment) e2.payment = "Payment method is required.";
    if (!paymentReceipt) e2.paymentReceipt = "Payment receipt is required.";
    if (Object.keys(e2).length) {
      setErrors((prev) => ({ ...prev, ...e2 }));
      return;
    }

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    const form = new FormData();
    form.append("subscriptionPlan", subscriptionPlan);
    form.append("payment", payment);
    form.append("paymentReceipt", paymentReceipt);

    setIsSubmitting(true);
    try {
      await axios.put(`${API_URL}/profiles/${userId}/renew-subscription`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Subscription request sent! Awaiting confirmation.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send the subscription request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sub-panel">
      <div className="sub-header">
        <button className="sub-header__back" onClick={handleBack} type="button" aria-label="Back">
          <ArrowLeft size={16} />
        </button>
        <p className="sub-header__title">
          {step === "plan" ? "Choose your subscription" : "Payment"}
        </p>
        <button className="sub-header__close" onClick={onBack} type="button" aria-label="Close">
          <X size={16} />
        </button>
      </div>

      <RenewStepIndicator step={step} />

      {step === "plan" ? (
        <div className="sub-body">
          <p className="sub-section-label">Duration</p>
          <div className="sub-plan-grid">
            {SUBSCRIPTION_PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`sub-plan-card${subscriptionPlan === plan.id ? " sub-plan-card--selected" : ""}`}
                onClick={() => {
                  setSubscriptionPlan(plan.id);
                  setErrors((er) => ({ ...er, subscriptionPlan: "" }));
                }}
              >
                {plan.badge && <span className="sub-plan-card__badge">{plan.badge}</span>}
                <div className="sub-plan-card__info">
                  <p className="sub-plan-card__label">{plan.label}</p>
                  <p className="sub-plan-card__permonth">{plan.perMonth} TND / month</p>
                </div>
                <p className="sub-plan-card__price">{plan.price} TND</p>
              </div>
            ))}
          </div>
          {errors.subscriptionPlan && <p className="sub-error">{errors.subscriptionPlan}</p>}
          <button type="button" className="sub-save" onClick={goToPayment}>
            Continue
          </button>
        </div>
      ) : (
        <form className="sub-body" onSubmit={handleSubmit} noValidate>
          <p className="sub-section-label">Payment method</p>
          <div className={`sub-field${errors.payment ? " sub-field--error" : ""}`}>
            <select
              value={payment}
              onChange={(e) => {
                setPayment(e.target.value);
                setErrors((er) => ({ ...er, payment: "" }));
              }}
            >
              <option value="">Choose a payment method</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <CreditCard size={16} className="sub-field__icon" />
          </div>
          {errors.payment && <p className="sub-error">{errors.payment}</p>}

          <p className="sub-section-label">Payment receipt</p>
          <label
            htmlFor="renewReceipt"
            className={`sub-upload${errors.paymentReceipt ? " sub-field--error" : ""}`}
          >
            <span className={`sub-upload__label${receiptName ? " sub-upload__label--filled" : ""}`}>
              {receiptName || "Upload the payment receipt"}
            </span>
            <Receipt size={16} className="sub-upload__icon" />
          </label>
          <input
            type="file"
            id="renewReceipt"
            accept="image/*,application/pdf"
            style={{ display: "none" }}
            onChange={handleReceiptChange}
          />
          {errors.paymentReceipt && <p className="sub-error">{errors.paymentReceipt}</p>}

          <button type="submit" className="sub-save" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Confirm and send"}
          </button>
        </form>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   News page
   ══════════════════════════════════════════════ */
const News = () => {
  const [view, setView] = useState("news"); // "news" | "subscribe"
  const [profileInfo, setProfileInfo] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  /* ── Fetch the subscription status of the logged-in client ── */
  useEffect(() => {
    const fetchProfileStatus = async () => {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      if (!userId || !token) {
        setLoadingProfile(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API_URL}/profiles/user/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfileInfo(data);
      } catch (err) {
        // 404 = no profile created yet, which is normal for a new client
        setProfileInfo(null);
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfileStatus();
  }, [API_URL]);

  const isSubscriptionActive =
    profileInfo?.isVerified &&
    profileInfo?.subscriptionStatus === "active" &&
    profileInfo?.subscriptionEndDate &&
    new Date(profileInfo.subscriptionEndDate) > new Date();

  const isSubscriptionPending =
    profileInfo?.isVerified && profileInfo?.subscriptionStatus === "pending";

  useEffect(() => {
    if (location.state?.showSubscriptionAlert) {
      toast.warn(
        "You are not subscribed or your subscription has expired. Please subscribe below to continue.",
        {
          position: "top-right",
          autoClose: 8000,
          hideProgressBar: false,
          style: {
            backgroundColor: "rgba(153, 60, 26, 0.9)",
            color: "white",
            fontSize: "13px",
            borderRadius: "8px",
            padding: "15px 20px",
            width: "280px",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          },
        }
      );

      // Clear the state so the toast is not shown again on every refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="news-container">
      <NavbarLog />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="news-wrapper" role="main" aria-label="News and announcements">
        <div className="news-popup news-popup--transform">
          {/* ── News view ── */}
          <div className={`news-view ${view === "news" ? "is-active" : "is-hidden"}`}>
            <h1>News & Announcements</h1>

            {NEWS_ITEMS.map((item, index) => (
              <React.Fragment key={item.id}>
                <div className="news-item">
                  <p className="news-item__date">{formatDate(item.date)}</p>
                  <h2>{item.title}</h2>

                  {item.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}

                  {item.bullets && (
                    <>
                      <p className="news-item__bullets-title">{item.bulletsTitle}</p>
                      <ul>
                        {item.bullets.map((b, i) => (
                          <li key={i}>
                            <strong>{b.label}:</strong> {b.text}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  
                </div>

                {index < NEWS_ITEMS.length - 1 && <div className="news-separator" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default News;